/**
 * Rule changes that wait for the round to end.
 *
 * Roulette, keno and craps open their next window the moment the last one
 * clears, so a host who only gets to edit "between rounds" barely gets to edit
 * at all. A change made mid-round is held on the room instead and lands the
 * next time the table is between rounds. The TV and phones play the rules in
 * force; the host panel shows the rules as they will be.
 */
import { normalizeBaccaratRules, type BaccaratRules } from './baccaratRules.js';
import { normalizeCrapsRules, type CrapsRules } from './crapsRules.js';
import { normalizeHoldemRules, type HoldemRules } from './holdemRules.js';
import { normalizeKenoRules, type KenoRules } from './kenoRules.js';
import { normalizeRouletteRules, type RouletteRules } from './rouletteRules.js';
import { normalizeRules, type TableRules } from './rules.js';
import { normalizeThreecardRules, type ThreecardRules } from './threecardRules.js';
import type { RoomState } from './types.js';

/** One rules set, named the way the host's rules events name it. */
export type RulesTarget = 'rules' | 'holdem' | 'baccarat' | 'roulette' | 'craps' | 'threecard' | 'keno';

/** The rules each target holds, as the room state spells them. */
export interface RulesByTarget {
  rules: TableRules;
  holdem: HoldemRules;
  baccarat: BaccaratRules;
  roulette: RouletteRules;
  craps: CrapsRules;
  threecard: ThreecardRules;
  keno: KenoRules;
}

/**
 * The next round's rules, for every set with a change waiting. A set with
 * nothing waiting is absent, so `{}` means the table plays on as it is.
 */
export type PendingRules = Partial<RulesByTarget>;

/** Where each target's rules sit on the room state. */
export const RULES_STATE_KEY = {
  rules: 'rules',
  holdem: 'holdemRules',
  baccarat: 'baccaratRules',
  roulette: 'rouletteRules',
  craps: 'crapsRules',
  threecard: 'threecardRules',
  keno: 'kenoRules',
} as const satisfies Record<RulesTarget, keyof RoomState>;

const NORMALIZE: {
  [T in RulesTarget]: (patch: Record<string, unknown>, current: RulesByTarget[T]) => RulesByTarget[T];
} = {
  rules: normalizeRules,
  holdem: normalizeHoldemRules,
  baccarat: normalizeBaccaratRules,
  roulette: normalizeRouletteRules,
  craps: normalizeCrapsRules,
  threecard: normalizeThreecardRules,
  keno: normalizeKenoRules,
};

/** Fold an untrusted patch into one target's rules, the way its event would. */
export function normalizeTargetRules<T extends RulesTarget>(
  target: T,
  patch: Record<string, unknown>,
  current: RulesByTarget[T],
): RulesByTarget[T] {
  return NORMALIZE[target](patch, current);
}

/**
 * The room as it will stand next round: every waiting rules set laid over the
 * one in force. The host panel draws from this, so a field shows the value the
 * host picked rather than snapping back until the round ends.
 */
export function withPendingRules(state: RoomState): RoomState {
  const pending = state.pendingRules;
  if (Object.keys(pending).length === 0) return state;
  const next: RoomState = { ...state };
  for (const target of Object.keys(pending) as RulesTarget[]) {
    const rules = pending[target];
    if (rules) (next as unknown as Record<string, unknown>)[RULES_STATE_KEY[target]] = rules;
  }
  return next;
}

/** True when this one rule changes next round. */
export function rulePending(state: RoomState, target: RulesTarget, key: string): boolean {
  const next = state.pendingRules[target] as Record<string, unknown> | undefined;
  if (!next) return false;
  const now = state[RULES_STATE_KEY[target]] as unknown as Record<string, unknown>;
  return JSON.stringify(next[key]) !== JSON.stringify(now[key]);
}
